import React from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid2,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  resolveOptionsSourceKey,
  resolveProductFormFieldName,
} from "./dynamicFormApi";
import { resolveProductSettingsFieldName } from "../../Settings/productFormFieldDefaults";
import "../../common/commonStyles.css";

const SKIP_TYPES = new Set(["repeater", "relation_table", "file", "display"]);
const SKIP_FIELD_NAMES = new Set([
  "product_type",
  "split",
  "cost",
  "box_cost",
  "piece_cost",
  "recommendedSellingBoxPrice",
  "recommended_selling_price",
  "recommendedSellingPiecePrice",
  "piece_recommended_selling_price",
  "price_box",
  "special_price_box",
  "price_piece",
  "special_price_piece",
  "margin",
  "box_margin",
  "piece_margin",
  "box_prices",
  "box_pricing",
  "piece_prices",
  "piece_pricing",
  "product_image",
  "suppliers",
  "supplier",
  "select_supplier",
  "select_suppliers",
  "selected_suppliers",
  "selected_supplier",
  "supplier_id",
  "supplier_ids",
  "band_1",
  "band_2",
  "band_3",
  "band_4",
  "band_5",
  "band_6",
]);

const LAYOUT_ROWS = [
  { type: "codeName", keys: ["code", "name"] },
  { type: "full", keys: ["details"] },
  { type: "pair", keys: ["size", "pack"] },
  { type: "pair", keys: ["unit", "tax_rate"] },
  { type: "triple", keys: ["min_alert_quantity", "max_alert_quantity", "maximum_order_quantity"] },
  { type: "pair", keys: ["brand", "category"] },
  { type: "dimensionPair", keys: ["length", "width"] },
  { type: "dimensionHalf", keys: ["height"] },
  { type: "triple", keys: ["cbm", "gross_weight", "net_weight"] },
  { type: "triple", keys: ["bay", "rack", "part_number"] },
  { type: "triple", keys: ["inner_ean_number", "outer_ean_number", "pallete_ean"] },
  { type: "pair", keys: ["new_arrival", "is_discount_allow"] },
  { type: "pair", keys: ["featured", "availableFor"] },
];

const humanizeFieldName = (name) =>
  String(name || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const requiredLabel = (text, required) => (
  <>
    {text}
    {required ? <span style={{ color: "red" }}> *</span> : null}
  </>
);

const getOptionValue = (option) => {
  if (option == null) return "";
  if (typeof option !== "object") return String(option);
  return String(option.value ?? option.id ?? option.key ?? option.name ?? "");
};

const getOptionLabel = (option) => {
  if (option == null) return "";
  if (typeof option !== "object") return String(option);
  return String(option.label ?? option.name ?? option.title ?? getOptionValue(option));
};

const buildLayoutRows = (fields) => {
  const byKey = new Map();
  fields.forEach((field) => {
    byKey.set(resolveProductFormFieldName(field.name), field);
  });

  const used = new Set();
  const rows = [];

  LAYOUT_ROWS.forEach((row) => {
    const rowFields = row.keys
      .map((key) => byKey.get(key) || null)
      .filter(Boolean);
    if (!rowFields.length) {
      return;
    }
    rowFields.forEach((field) => used.add(resolveProductFormFieldName(field.name)));
    rows.push({ ...row, fields: rowFields });
  });

  const leftovers = fields.filter(
    (field) => !used.has(resolveProductFormFieldName(field.name))
  );
  leftovers.forEach((field) => {
    if (field.unitField) {
      const unitKey = resolveProductFormFieldName(field.unitField);
      const unitField = byKey.get(unitKey);
      rows.push({
        type: "dimensionHalf",
        fields: unitField ? [field, unitField] : [field],
      });
      used.add(resolveProductFormFieldName(field.name));
      if (unitField) used.add(unitKey);
      return;
    }
    if (field.name.endsWith("_unit")) {
      return;
    }
    rows.push({ type: "full", fields: [field] });
  });

  return rows;
};

const DynamicProductFormFields = ({
  fields = [],
  formData = {},
  errors = {},
  onChange,
  isReadOnly = false,
  optionsBySource = {},
}) => {
  const visibleFields = fields.filter((field) => {
    const resolved = resolveProductFormFieldName(field.name);
    const settingsName = resolveProductSettingsFieldName(field.name);
    return (
      field.visible !== false &&
      !SKIP_TYPES.has(field.type) &&
      !SKIP_FIELD_NAMES.has(field.name) &&
      !SKIP_FIELD_NAMES.has(resolved) &&
      !SKIP_FIELD_NAMES.has(settingsName) &&
      !(field.fields || []).length &&
      !String(field.name).endsWith("_unit")
    );
  });
  if (!visibleFields.length) {
    return null;
  }

  const fieldByKey = buildFieldMap(fields);

  const resolveOptions = (field) => {
    if (!field) return [];
    if (field.options?.length) {
      return field.options;
    }
    const sourceKey = resolveOptionsSourceKey(field.optionsSource);
    return optionsBySource[sourceKey] || optionsBySource[field.optionsSource] || [];
  };

  const emitChange = (name, value) => {
    onChange?.({
      target: { name, value },
    });
  };

  const renderLabeledField = (field, { hideLabel = false, usePlaceholder = false } = {}) => {
    if (!field) return null;
    const formKey = resolveProductFormFieldName(field.name);
    const value = formData[formKey] ?? "";
    const error = errors[formKey] || "";
    const readOnly = isReadOnly || field.readOnly;
    const options = resolveOptions(field);
    const caption = String(field.label || "").trim();
    const fallbackCaption =
      String(field.placeholder || "").trim() || caption || humanizeFieldName(formKey);

    if (field.type === "checkbox") {
      return (
        <Box key={field.name} sx={{ width: "100%" }}>
          <FormControlLabel
            control={
              <Checkbox
                className="checkBox"
                name={formKey}
                checked={value === "1" || value === 1 || value === true}
                onChange={(e) => emitChange(formKey, e.target.checked ? "1" : "0")}
                disabled={readOnly}
              />
            }
            label={
              <>
                {field.label}
                {field.required ? <span style={{ color: "red" }}> *</span> : null}
              </>
            }
          />
          {error ? (
            <Typography variant="caption" sx={{ color: "error.main", display: "block", ml: 4 }}>
              {error}
            </Typography>
          ) : null}
        </Box>
      );
    }

    if (field.type === "select" || field.type === "dropdown") {
      const selected =
        options.find((option) => String(getOptionValue(option)) === String(value)) ||
        null;
      return (
        <Box key={field.name} sx={{ width: "100%" }}>
          {!hideLabel && caption ? (
            <Typography className="productForm-lables">
              {caption}
              {field.required ? <span style={{ color: "red" }}> *</span> : null}
            </Typography>
          ) : null}
          <Autocomplete
            size="small"
            options={options}
            value={selected}
            disabled={readOnly}
            getOptionLabel={(option) => getOptionLabel(option)}
            isOptionEqualToValue={(option, selectedValue) =>
              String(getOptionValue(option)) === String(getOptionValue(selectedValue))
            }
            onChange={(_, newValue) =>
              emitChange(formKey, newValue ? getOptionValue(newValue) : "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                className="form-input-border"
                placeholder={
                  hideLabel || !caption
                    ? ""
                    : field.placeholder || `Select ${caption}`
                }
                error={!!error}
                helperText={error}
              />
            )}
          />
        </Box>
      );
    }

    const multiline = field.type === "textarea" || field.type === "multiline";
    const inputType =
      field.type === "number" || field.type === "email" || field.type === "password"
        ? field.type
        : "text";
    const showFloatingLabel = usePlaceholder || hideLabel || !caption;

    return (
      <Box key={field.name} sx={{ width: "100%" }}>
        {!hideLabel && !usePlaceholder && caption ? (
          <Typography className="productForm-lables">
            {caption}
            {field.required ? <span style={{ color: "red" }}> *</span> : null}
          </Typography>
        ) : null}
        <TextField
          fullWidth
          size="small"
          name={formKey}
          type={inputType}
          value={value}
          label={showFloatingLabel ? requiredLabel(fallbackCaption, field.required) : undefined}
          placeholder={!showFloatingLabel ? field.placeholder || "" : undefined}
          onChange={(e) => emitChange(formKey, e.target.value)}
          multiline={multiline}
          rows={multiline ? 2 : undefined}
          error={!!error}
          helperText={error}
          className="form-input-border text-field"
          inputProps={{
            maxLength: field.maxLength || (formKey === "code" ? 10 : undefined),
            min: field.min,
            max: field.max,
            step: field.step,
            readOnly,
          }}
          onWheel={inputType === "number" ? (e) => e.target.blur() : undefined}
        />
      </Box>
    );
  };

  const renderDimensionComposite = (mainField) => {
    if (!mainField) return null;
    const formKey = resolveProductFormFieldName(mainField.name);
    const unitName = mainField.unitField || `${mainField.name}_unit`;
    const unitField = fieldByKey.get(resolveProductFormFieldName(unitName)) ||
      fieldByKey.get(unitName);
    const unitKey = resolveProductFormFieldName(unitField?.name || unitName);
    const unitOptions = resolveOptions(unitField);
    const fallbackUnits = [
      { label: "MM", value: "mm" },
      { label: "CM", value: "cm" },
      { label: "M", value: "m" },
    ];
    const options = unitOptions.length ? unitOptions : fallbackUnits;
    const caption = String(mainField.label || "").trim();
    const fallbackCaption =
      String(mainField.placeholder || "").trim() || caption || humanizeFieldName(formKey);

    return (
      <Box
        key={mainField.name}
        sx={{ display: "flex", gap: 1, flexDirection: { xs: "column", sm: "row" }, width: "100%" }}
      >
        <TextField
          label={requiredLabel(fallbackCaption, mainField.required)}
          size="small"
          name={formKey}
          type="number"
          value={formData[formKey] ?? ""}
          onChange={(e) => emitChange(formKey, e.target.value)}
          sx={{ flex: 1 }}
          className="form-input-border text-field"
          fullWidth
          error={!!errors[formKey]}
          helperText={errors[formKey] || ""}
          onWheel={(e) => e.target.blur()}
        />
        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 75 } }}>
          <Select
            className="input-border"
            name={unitKey}
            value={formData[unitKey] ?? unitField?.defaultValue ?? "mm"}
            onChange={(e) => emitChange(unitKey, e.target.value)}
            sx={{ fontSize: 13, height: 35 }}
          >
            {options.map((option) => (
              <MenuItem key={getOptionValue(option)} value={getOptionValue(option)}>
                {getOptionLabel(option)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const layoutRows = buildLayoutRows(visibleFields);
  const packedFields = [];
  const specialBlocks = [];

  layoutRows.forEach((row, rowIndex) => {
    if (row.type === "codeName" || row.type === "full") {
      specialBlocks.push({ kind: "special", row, rowIndex, before: packedFields.length });
      return;
    }
    if (row.type === "dimensionPair" || row.type === "dimensionHalf") {
      specialBlocks.push({ kind: "dimension", row, rowIndex, before: packedFields.length });
      return;
    }
    row.fields.forEach((field) => packedFields.push(field));
  });

  const renderPackedGrid = (fieldsList, keyPrefix) => {
    if (!fieldsList.length) return null;
    const placeholderKeys = new Set([
      "cbm",
      "gross_weight",
      "net_weight",
      "bay",
      "rack",
      "part_number",
      "inner_ean_number",
      "outer_ean_number",
      "pallete_ean",
    ]);
    return (
      <Grid2 container spacing={1} sx={{ mb: 1 }} key={keyPrefix}>
        {fieldsList.map((field) => {
          const formKey = resolveProductFormFieldName(field.name);
          const usePlaceholder = placeholderKeys.has(formKey);
          return (
            <Grid2 key={field.name} size={{ xs: 12, sm: 6 }}>
              {renderLabeledField(field, {
                hideLabel: usePlaceholder,
                usePlaceholder,
              })}
            </Grid2>
          );
        })}
      </Grid2>
    );
  };

  const renderSpecialRow = (row, rowIndex) => {
    if (row.type === "codeName") {
      const codeField = row.fields.find(
        (field) => resolveProductFormFieldName(field.name) === "code",
      );
      const nameField = row.fields.find(
        (field) => resolveProductFormFieldName(field.name) === "name",
      );
      const onlyField = !codeField || !nameField ? row.fields[0] : null;
      if (onlyField && !(codeField && nameField)) {
        return (
          <Box key={`row-${rowIndex}`} sx={{ mb: 1.5 }}>
            {renderLabeledField(onlyField)}
          </Box>
        );
      }
      return (
        <Box
          key={`row-${rowIndex}`}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 1,
            mb: 1.5,
          }}
        >
          {codeField ? (
            <Box sx={{ flex: 1, maxWidth: { xs: "100%", md: "30%" } }}>
              {renderLabeledField(codeField)}
            </Box>
          ) : null}
          {nameField ? (
            <Box sx={{ flex: 2 }}>{renderLabeledField(nameField)}</Box>
          ) : null}
        </Box>
      );
    }

    if (row.type === "dimensionPair" || row.type === "dimensionHalf") {
      return (
        <Grid2 container spacing={1} sx={{ mb: 1 }} key={`row-${rowIndex}`}>
          {row.fields.map((field) => (
            <Grid2 key={field.name} size={{ xs: 12, sm: 6 }}>
              {renderDimensionComposite(field)}
            </Grid2>
          ))}
        </Grid2>
      );
    }

    return (
      <Box key={`row-${rowIndex}`} sx={{ mb: 1.5 }}>
        {row.fields.map((field) => renderLabeledField(field))}
      </Box>
    );
  };

  const blocks = [];
  let cursor = 0;
  specialBlocks.forEach((block) => {
    if (block.before > cursor) {
      blocks.push({
        kind: "packed",
        fields: packedFields.slice(cursor, block.before),
        key: `packed-${cursor}`,
      });
    }
    blocks.push(block);
    cursor = block.before;
  });
  if (cursor < packedFields.length) {
    blocks.push({
      kind: "packed",
      fields: packedFields.slice(cursor),
      key: `packed-${cursor}`,
    });
  }

  return (
    <Box>
      {blocks.map((block) => {
        if (block.kind === "packed") {
          return renderPackedGrid(block.fields, block.key);
        }
        return renderSpecialRow(block.row, block.rowIndex);
      })}
    </Box>
  );
};

const buildFieldMap = (fields) => {
  const map = new Map();
  fields.forEach((field) => {
    map.set(resolveProductFormFieldName(field.name), field);
    map.set(field.name, field);
  });
  return map;
};

export default DynamicProductFormFields;
